import { DefaultServerService, type Server } from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { FrameworkScope, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import { AuthJourneyShell, getSignInJourneyViewState } from '../auth-journey';
import { AddSelfhostedStep } from './add-selfhosted';
import { SignInStep } from './sign-in';
import { SignInWithEmailStep } from './sign-in-with-email';
import { SignInWithTotpStep } from './sign-in-with-totp';

export { SignInWithTotpStep } from './sign-in-with-totp';

export type SignInStep =
  | 'signIn'
  | 'signInWithEmail'
  | 'signInWithTotp'
  | 'addSelfhosted';

export type SignInIntent = 'default' | 'invite' | 'recovery' | 'registration';

export interface SignInState {
  step: SignInStep;
  server?: Server;
  initialServerBaseUrl?: string;
  email?: string;
  name?: string;
  redirectUrl?: string;
  intent?: SignInIntent;
  /** sl-qsjm.4: Preferred 2FA method after password login */
  preferredTwoFactorMethod?: 'totp' | 'email';
  emailCodeSent?: boolean;
}

export const SignInPanel = ({
  server: initialServerBaseUrl,
  initStep,
  onAuthenticated,
  planContext,
  initialEmail,
  redirectUrl,
  intent = 'default',
}: {
  onAuthenticated?: (status: AuthSessionStatus) => void;
  server?: string;
  initStep?: SignInStep | undefined;
  planContext?: string | null;
  initialEmail?: string;
  redirectUrl?: string | null;
  intent?: SignInIntent;
}) => {
  const [state, setState] = useState<SignInState>({
    step: initStep
      ? initStep
      : initialServerBaseUrl
        ? 'addSelfhosted'
        : 'signIn',
    initialServerBaseUrl: initialServerBaseUrl,
    email: initialEmail,
    redirectUrl: redirectUrl ?? undefined,
    intent,
  });

  const defaultServerService = useService(DefaultServerService);

  const step = state.step;
  const server = state.server ?? defaultServerService.server;
  const journeyViewState = getSignInJourneyViewState(
    step,
    state.intent ?? intent,
    state.emailCodeSent ?? false
  );
  const showJourney = step === 'signInWithEmail' || step === 'signInWithTotp';
  const handleAuthenticated = useCallback(
    (status: AuthSessionStatus) => {
      onAuthenticated?.(status);
    },
    [onAuthenticated]
  );

  const content = (
    <FrameworkScope scope={server.scope}>
      {step === 'signIn' ? (
        <SignInStep
          state={state}
          changeState={setState}
          onAuthenticated={handleAuthenticated}
          planContext={planContext}
        />
      ) : step === 'signInWithEmail' ? (
        <SignInWithEmailStep
          state={state}
          changeState={setState}
          onAuthenticated={handleAuthenticated}
        />
      ) : step === 'signInWithTotp' ? (
        <SignInWithTotpStep
          state={state}
          changeState={setState}
          onAuthenticated={handleAuthenticated}
        />
      ) : step === 'addSelfhosted' ? (
        <AddSelfhostedStep state={state} changeState={setState} />
      ) : null}
    </FrameworkScope>
  );

  if (showJourney) {
    return (
      <AuthJourneyShell viewState={journeyViewState}>
        {content}
      </AuthJourneyShell>
    );
  }

  return (
    <div
      data-testid="sign-in-entry-card"
      style={{ maxWidth: '400px', width: '100%' }}
    >
      {content}
    </div>
  );
};
